import { isHttpClientError } from "../../../lib/http/errors";
import { applyTransportSelection, hasPersistedTransportFacts, transportEndpointDraft } from "./itineraryAdvancedService";
import { createRailwayTransferPlan, railwayTransferUnavailableReason } from "./itineraryRailwayTransferQueryService";
import { calculatedTransportDurationMinutes } from "./itineraryTransportTime";
import type { FlightQuery, FlightSearchResult, ItineraryNode, ItinerarySegment, RailwayQuery, RailwaySearchResult, RailwayTransferPlan, RailwayTransferSearchResult, TimeSlot, TransportEndpointChange, TransportMode, TransportOption, TransportSelection } from "../types";

const TRANSPORT_SELECTION_LABEL_MAX_LENGTH = 300;

type TimetableQueries = {
  getRailwayOptions?: (query: RailwayQuery, signal?: AbortSignal) => Promise<RailwaySearchResult>;
  getRailwayTransfers?: (query: RailwayQuery, signal?: AbortSignal) => Promise<RailwayTransferSearchResult>;
  getFlightOptions?: (query: FlightQuery, signal?: AbortSignal) => Promise<FlightSearchResult>;
};

export type AutomaticTimetableTimeSlotConstraint = {
  departureSlot?: TimeSlot;
};

/** 新补站点关联首个可用班次；前后插区域铁路有效直达空结果再补查一次完整中转。 */
export async function queryAutomaticTimetable(
  segment: ItinerarySegment,
  from: ItineraryNode,
  to: ItineraryNode,
  travelDate: string | null | undefined,
  queries: TimetableQueries,
  signal: AbortSignal,
  now: () => string,
  timeSlotConstraint?: AutomaticTimetableTimeSlotConstraint,
): Promise<{ segment: ItinerarySegment; endpointChanges: TransportEndpointChange[]; railwayTransfer?: RailwayTransferPlan } | null> {
  const isRail = segment.transportMode === "rail";
  const nodeType = isRail ? "railway_station" : "airport";
  if ((segment.transportMode !== "rail" && segment.transportMode !== "air")
    || from.nodeType !== nodeType || to.nodeType !== nodeType
    || !from.pointSpuId?.trim() || !to.pointSpuId?.trim()) return null;
  const date = normalizeTransportDate(segment.transportMode, travelDate || localDateIso());
  const canFallback = isRail && Boolean(segment.sourcePayload?.railwayInsertionPlacement)
    && Boolean(from.railwayRegionName?.trim() && to.railwayRegionName?.trim()) && !hasPersistedTransportFacts(segment);
  const fallback = async () => {
    if (!canFallback || !queries.getRailwayTransfers || signal.aborted) return null;
    const query: RailwayQuery = { fromStationIds: [from.pointSpuId!], toStationIds: [to.pointSpuId!], date, queryScope: "region" };
    const result = await queries.getRailwayTransfers(query, signal);
    if (signal.aborted) return null;
    const option = result.transfers.find((item) => !railwayTransferUnavailableReason(item)
      && (!timeSlotConstraint?.departureSlot || transportTimeSlot(item.legs[0].departureAt) === timeSlotConstraint.departureSlot)
      && item.legs.every((leg) => calculatedTransportDurationMinutes(leg.departureAt, leg.arrivalAt) !== null)
      && calculatedTransportDurationMinutes(item.legs[0].arrivalAt, item.legs[1].departureAt) !== null);
    if (!option) return null;
    const seats = Object.fromEntries(option.legs.map((leg) => [leg.optionId, selectedSeat(leg, {})?.seatClass ?? ""]));
    const railwayTransfer = createRailwayTransferPlan(option, result, seats, {
      travelDate, queryKey: `${from.pointSpuId}-${to.pointSpuId}-${date}`, queriedAt: now(), queryScope: "region",
    });
    const first = option.legs[0]; const last = option.legs[1];
    // 首末时刻用于既有插入归位；展开后每腿保存自己的车次事实，不能清掉一次性插入标记。
    return { segment: { ...segment, departureAt: railwayTransfer.legs[0].departureAt, arrivalAt: railwayTransfer.legs[1].arrivalAt },
      railwayTransfer, endpointChanges: railwayEndpointChanges(from, to, undefined, {
      fromStationId: first.fromStationId, fromName: first.fromName, toStationId: last.toStationId, toName: last.toName,
    }) };
  };
  let result: RailwaySearchResult | FlightSearchResult | undefined;
  try {
    result = isRail ? await queries.getRailwayOptions?.({
      fromStationIds: [from.pointSpuId], toStationIds: [to.pointSpuId], date, details: true,
      ...(from.railwayRegionName && to.railwayRegionName ? { queryScope: "region" as const } : {}),
    }, signal)
    : await queries.getFlightOptions?.({ origin: from.title, destination: to.title, departureDate: date }, signal);
  } catch (error) {
    if (canFallback && isHttpClientError(error) && error.statusCode === 404 && error.code === "ITINERARY_TRANSPORT_EMPTY") return fallback();
    throw error;
  }
  if (!result || signal.aborted) return null;
  if (result.options.length === 0) return fallback();
  // 新增具体站点按站名配车；跨时段重查、大区域和工作区查询保持既有范围。
  const matchStationNames = isRail && !timeSlotConstraint?.departureSlot
    && !(from.railwayRegionName?.trim() && to.railwayRegionName?.trim());
  const options = result.options.filter((item) => (
    (!timeSlotConstraint?.departureSlot || transportTimeSlot(item.departureAt) === timeSlotConstraint.departureSlot)
    && (!matchStationNames
      || (item.fromName?.trim().replace(/站$/, "") === from.title.trim().replace(/站$/, "")
        && item.toName?.trim().replace(/站$/, "") === to.title.trim().replace(/站$/, "")))
  ));
  const option = options.find(canSelectTransportOption);
  if (!option) return null;
  const referenceReason = "referenceReason" in result ? result.referenceReason : null;
  const warnings = result.isReference
    ? [isRail
      ? railwayReferenceNotice(result as RailwaySearchResult)
      : referenceReason === "query_failed"
      ? `原航班查询日期 ${result.requestedDate ?? date} 未返回可用航班，已自动改查明天 ${result.referenceDate ?? result.queryDate ?? date} 的航班，仅供参考`
      : `请求日 ${result.requestedDate ?? date} 超出可查询范围，已改用 ${result.referenceDate ?? result.queryDate ?? date} 的数据，仅供参考`]
    : [];
  if (travelDate && date !== travelDate) warnings.push(`查询日 ${date} 仅用于参考，已按行程日 ${travelDate} 写入班次时刻`);
  const railway = isRail ? result as RailwaySearchResult : null;
  const endpointChanges = railway ? railwayEndpointChanges(from, to, railway, option) : [];
  const originId = option.fromStationId ?? railway?.originStation?.refId ?? from.pointSpuId;
  const destinationId = option.toStationId ?? railway?.destinationStation?.refId ?? to.pointSpuId;
  const selectedSegment = applyTransportSelection(segment, alignTransportSelectionDate(selection(option, selectedSeat(option, {})), travelDate), [], {
    provider: option.provider,
    queriedAt: now(),
    queryKey: isRail ? `${originId}-${destinationId}-${date}` : `${from.title}-${to.title}-${date}`,
    railwayQueryScope: isRail ? railway?.regionQuery ? "region" : "station" : null,
    options: result.options.map((item) => selection(item, selectedSeat(item, {}))),
    isReference: Boolean(result.isReference),
    referenceDate: result.referenceDate ?? null,
    warnings,
  });
  delete selectedSegment.automaticQueryDepartureSlot;
  return { endpointChanges, segment: selectedSegment };
}

/** 按本地班次钟点划分上午、下午与 18 点起的晚上。 */
function transportTimeSlot(value?: string | null): TimeSlot | null {
  const hour = value?.match(/T([01]\d|2[0-3]):[0-5]\d/)?.[1];
  if (!hour) return null;
  return Number(hour) < 12 ? "morning" : Number(hour) < 18 ? "afternoon" : "evening";
}

/** 按主选车次的实际站对变更端点，保留节点拓扑；旧区域结果兼容整批站点。 */
export function railwayEndpointChanges(
  from: ItineraryNode, to: ItineraryNode, result?: RailwaySearchResult | null,
  selected?: Pick<TransportOption, "fromStationId" | "toStationId" | "fromName" | "toName"> | null,
): TransportEndpointChange[] {
  for (const [node, refId, name] of [
    [from, selected?.fromStationId, selected?.fromName],
    [to, selected?.toStationId, selected?.toName],
  ] as const) {
    if (refId === null && name && name.replace(/站$/, "") !== node.title.replace(/站$/, "")) {
      throw new Error(`车次实际车站“${name}”未匹配到启用的火车站主数据，请维护后重新查询`);
    }
  }
  const origin = selected?.fromStationId && selected.fromName
    ? { refId: selected.fromStationId, name: selected.fromName } : result?.regionQuery ? result.originStation : null;
  const destination = selected?.toStationId && selected.toName
    ? { refId: selected.toStationId, name: selected.toName } : result?.regionQuery ? result.destinationStation : null;
  if (result?.regionQuery && (!origin || !destination)) throw new Error("区域线路缺少实际上下车站，请重新查询");
  return [
    { node: from, station: origin },
    { node: to, station: destination },
  ].flatMap(({ node, station }) => !station || node.pointSpuId === station.refId
    && (node.title === station.name || !node.railwayRegionName && !result?.regionQuery) ? [] : [{
    nodeId: node.id,
    previous: transportEndpointDraft(node),
    draft: { ...transportEndpointDraft(node), nodeType: "railway_station" as const, pointSpuId: station.refId,
      title: station.name, stayAreaId: null, organizationSiteId: null, description: null, imageUrl: null },
  }]);
}

/** 将交通查询选项投影为可持久化的受控选择。 */
export function selection(option: TransportOption, seat?: TransportOption["seats"][number]): TransportSelection {
  return {
    provider: option.provider,
    optionId: option.optionId,
    label: Array.from(
      `${option.routeCode} ${option.fromName ?? ""}-${option.toName ?? ""}`.trim(),
    ).slice(0, TRANSPORT_SELECTION_LABEL_MAX_LENGTH).join(""),
    routeCode: option.routeCode,
    fromName: option.fromName ?? null,
    toName: option.toName ?? null,
    ...(option.fromStationId !== undefined ? { fromStationId: option.fromStationId } : {}),
    ...(option.toStationId !== undefined ? { toStationId: option.toStationId } : {}),
    price: String(seat?.price ?? option.price ?? "") || null,
    seatClass: option.provider === "py12306" ? seat?.seatClass ?? null : null,
    cabinClass: option.provider === "flyai" ? seat?.seatClass ?? null : null,
    departureAt: option.departureAt ?? null,
    arrivalAt: option.arrivalAt ?? null,
    durationMinutes: option.durationMinutes ?? null,
  };
}

/** 参考日查询只改变查询条件，确认时保留班次时刻并映射回路线所属行程日。 */
export function alignTransportSelectionDate(
  value: TransportSelection,
  travelDate?: string | null,
): TransportSelection {
  if (!travelDate || !value.departureAt) return value;
  const departureDate = isoDatePrefix(value.departureAt);
  if (!departureDate) return value;
  const departureAt = replaceIsoDate(value.departureAt, travelDate);
  const arrivalDate = isoDatePrefix(value.arrivalAt);
  const arrivalDayOffset = arrivalDate
    ? differenceInCalendarDays(departureDate, arrivalDate)
    : null;
  const arrivalAt = value.arrivalAt && arrivalDayOffset !== null
    ? replaceIsoDate(value.arrivalAt, addIsoDays(travelDate, arrivalDayOffset))
    : value.arrivalAt;
  return { ...value, departureAt, arrivalAt };
}

/** 提取 ISO 日期时间中的日期前缀。 */
function isoDatePrefix(value?: string | null) {
  const date = value?.slice(0, 10) ?? "";
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
}

/** 保留时刻与时区部分，仅替换 ISO 日期前缀。 */
function replaceIsoDate(value: string, date: string) {
  return `${date}${value.slice(10)}`;
}

/** 按 UTC 日历日计算两个 ISO 日期的天数差。 */
function differenceInCalendarDays(fromDate: string, toDate: string) {
  const from = Date.parse(`${fromDate}T00:00:00Z`);
  const to = Date.parse(`${toDate}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  return Math.round((to - from) / 86_400_000);
}

/** 在 ISO 日期上增加指定日历天数。 */
export function addIsoDays(date: string, days: number) {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

/** 按日历月增加日期，月末日期在目标月收敛到最后一天。 */
export function addIsoMonths(date: string, months: number) {
  const value = new Date(`${date}T00:00:00Z`);
  const originalDay = value.getUTCDate();
  value.setUTCDate(1);
  value.setUTCMonth(value.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth() + 1, 0)).getUTCDate();
  value.setUTCDate(Math.min(originalDay, lastDay));
  return value.toISOString().slice(0, 10);
}

/** 返回用户选择或按铁路业务优先级得到的席别/舱位。 */
export function selectedSeat(option: TransportOption, selected: Record<string, string>) {
  const explicit = option.seats.find((seat) => seat.seatClass === selected[transportSelectionKey(option)]);
  if (explicit && isPurchasableSeat(explicit)) return explicit;
  const purchasable = option.seats.filter(isPurchasableSeat);
  if (option.provider !== "py12306") return purchasable[0];
  const preferred = ["二等座", "硬卧", "软卧"]
    .map((name) => purchasable.find((seat) => seat.seatClass === name))
    .find(Boolean);
  return preferred ?? purchasable[0];
}

/** 无席别方案按方案价可选；有席别时至少保留一个可购买席别。 */
export function canSelectTransportOption(option: TransportOption) {
  return option.seats.length === 0 || option.seats.some(isPurchasableSeat);
}

/** 明确不可售的席别继续展示，但不能被选择或恢复。 */
export function isPurchasableSeat(seat: TransportOption["seats"][number]) {
  const availability = seat.availability?.trim() ?? "";
  if (!availability) return true;
  if (/^\d+$/.test(availability)) return Number(availability) > 0;
  return !/^(?:无|无票|候补|售罄|已售罄|不可售)$/.test(availability);
}

/** 用来源与稳定方案 ID 唯一标识查询结果。 */
export function transportSelectionKey(value: Pick<TransportSelection, "provider" | "optionId">) {
  return `${value.provider}\u0000${value.optionId}`;
}

/** 按浏览器本地时区返回今天，避免 UTC 日期在东八区跨日时误判。 */
export function localDateIso(now = new Date()) {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** 将交通查询日期限制在当前方式允许的窗口内，历史越界值按今天恢复。 */
export function normalizeTransportDate(mode: TransportMode | undefined, value: string) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value) || mode === "bus" || mode === "walk") return value;
  const today = localDateIso();
  const maxDate = mode === "rail" ? addIsoDays(today, 14) : addIsoMonths(today, 2);
  if (mode === "rail" && value > maxDate) return maxDate;
  return value < today || value > maxDate ? today : value;
}

/** 同时适用于预售窗裁剪和空结果递进，明确本次实际查询的数据日期。 */
export function railwayReferenceNotice(result: RailwaySearchResult): string {
  return `火车原查询日期 ${result.requestedDate}，已自动改用 ${result.referenceDate ?? result.queryDate} 的车次，仅供参考。`;
}
